import AddCommentIcon from "@mui/icons-material/AddComment";
import CloseIcon from "@mui/icons-material/Close";
import CommentIcon from "@mui/icons-material/Comment";
import DoneIcon from "@mui/icons-material/Done";
import SearchIcon from "@mui/icons-material/Search";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import EmailAddress from "_shared/email-addresses/email-address";
import EntityMemberAPIService from "_shared/entity-members/_api/entity-member-api-service";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import Name from "_shared/names/name";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { CanceledError } from "axios";
import EntityName from "legal-entities/entity-names/entity-name";
import { debounce } from "lodash";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { individualsApi } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { enqueueSnackbar } from "notistack";
import React, { ChangeEvent, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { Masked } from "work/_shared/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { getUpdatedProposalBuilderByProposalId } from "work/proposals/_store/proposals-redux-slice";
import { getArePendingComments } from "work/proposals/comments/_store/comments-redux-slice";
import Proposal from "work/proposals/proposal";
import ReviewerMessagePopover from "work/proposals/reviewers/_views/reviewer-message-popover";
import ProposalReviewer from "../proposal-reviewer";

const PopoverContainer = styled("div")(({ theme }) => ({
  minWidth: "25rem",
  padding: theme.spacing(2),
  textAlign: "center",
  width: "min-content",
}));
const SearchField = styled(TextField)(() => ({
  width: "100%",
})) as typeof TextField;
const LoaderProgress = styled(CircularProgress)(({ theme }) => ({
  display: "inline-block",
  marginBottom: theme.spacing(4),
  marginTop: theme.spacing(4),
}));
const ListContainer = styled(List)(() => ({
  maxHeight: "20rem",
  overflowX: "hidden",
  overflowY: "auto",
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
  gridTemplateColumns: "1fr 1fr",
  width: "100%",
  "& .MuiButton-root": {
    fontSize: "0.8em",
  },
}));
const SelectedIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  opacity: "0.75",
  position: "absolute",
  zIndex: 1,
}));
const EditingSwitch = styled(FormControlLabel)(() => ({
  alignItems: "flex-end",
  "& .MuiFormControlLabel-label": {
    fontSize: "0.7em",
  },
}));
const ReviewerAvatar = styled(ListItemAvatar)(() => ({
  position: "relative",
}));
const ActionsContainer = styled("div")(() => ({
  alignItems: "flex-end",
  display: "flex",
}));
const MessageLabel = styled(Typography)(() => ({
  fontSize: "0.7em",
  lineHeight: "1.5em",
}));

type ReviewerSelectorProps = {
  proposal?: Proposal;
  popoverAnchor?: HTMLButtonElement;
  onReviewersChanged?: (reviewers: ProposalReviewer[]) => void;
  onSend?: (reviewers: ProposalReviewer[]) => void;
  onPopoverClose?: () => void;
};

export default function ReviewerSelector(props: Readonly<ReviewerSelectorProps>) {
  const { proposal, popoverAnchor, onReviewersChanged, onSend, onPopoverClose } = props;
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  const updatedBuilderJSON = useSelector(getUpdatedProposalBuilderByProposalId(proposal?.id ?? Guid.EMPTY));
  const updatedBuilder = useMemo(() => {
    return ProposalBuilder.fromJSON({
      json: updatedBuilderJSON,
      session,
    });
  }, [session, updatedBuilderJSON]);

  const arePendingComments = useSelector(getArePendingComments(proposal ?? null));
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [reviewerPool, setReviewerPool] = React.useState<ProposalReviewer[] | undefined>(undefined);
  const [reviewerPoolIndividuals, setReviewerPoolIndividuals] = React.useState<Individual[]>([]);
  const [reviewerResults, setReviewerResults] = React.useState<ProposalReviewer[]>([]);
  const [currentReviewerEditing, setCurrentReviewerEditing] = React.useState<ProposalReviewer>();
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [popoverAnchorElement, setPopoverAnchorElement] = React.useState<HTMLElement | null>(null);
  const builderReviewers = useMemo(() => {
    if (!popoverAnchor) return [];
    return session.context?.viewingAsVendor
      ? (updatedBuilder?.currentSpec.vendorReviewers.map((r) => new ProposalReviewer(r)) ??
          proposal?.vendorReviewers ??
          [])
      : (updatedBuilder?.currentSpec.clientReviewers.map((r) => new ProposalReviewer(r)) ??
          proposal?.clientReviewers ??
          []);
  }, [
    session.context?.viewingAsVendor,
    popoverAnchor,
    updatedBuilder?.currentSpec.vendorReviewers,
    updatedBuilder?.currentSpec.clientReviewers,
    proposal?.vendorReviewers,
    proposal?.clientReviewers,
  ]);
  const [originalReviewers, setOriginalReviewers] = React.useState<ProposalReviewer[]>(builderReviewers ?? []);
  const [selectedReviewers, setSelectedReviewers] = React.useState<ProposalReviewer[]>(
    updatedBuilder?.currentSpec.vendorReviewers ?? [],
  );

  const getReviewers = React.useCallback(
    async (abortController: AbortController) => {
      setIsLoading(true);

      try {
        // Get entity members
        const entityMemberService = new EntityMemberAPIService({
          session,
          abortController,
        });
        const entityMemberInfo: EntityMember[] =
          EntityMember.fromJSONArray({
            jsonArray: await entityMemberService.getMembersForLegalEntity(session.user!.legalEntityId),
          }) ?? []; // TODO: use redux store

        // Get vendor-team members
        let teamMembers: EntityMember[];
        if (
          Masked.isMasked(updatedBuilder?.currentSpec.workAgreement.vendorTeam) ||
          Masked.isMasked(proposal?.vendorTeam)
        ) {
          teamMembers = [];
        } else {
          teamMembers =
            updatedBuilder?.currentSpec.workAgreement.vendorTeam?.members ?? proposal?.vendorTeam?.members ?? [];
        }

        const teamMemberUserIds = teamMembers.map((member) => member.userId);
        const teamMemberIndividuals: Individual[] =
          teamMemberUserIds.length > 0
            ? (Individual.fromJSONArray(
                await dispatch(
                  individualsApi.endpoints.getIndividualsByUserIds.initiate({
                    userIds: new Set(teamMemberUserIds.map((id) => id.value)),
                  }),
                ).unwrap(),
              ) ?? [])
            : [];
        for (const member of teamMemberIndividuals) {
          const entityMember = entityMemberInfo.find((e) => e.userId.isEqualTo(member.userId));
          if (!entityMember) {
            let memberInfo = {
              ...member,
              email: member.profile?.email ?? new EmailAddress("unknown@example.com"),
              entityId: member.legalEntityId ?? Guid.generate(),
              entityName: new EntityName(member.company?.profile?.name ?? "Unknown"),
              individualId: member.id,
              isAdmin: false,
              isOfficer: false,
              isActive: true,
              memberId: member.id,
              name: Name.create({ params: member.profile }),
              userId: member.userId ?? undefined,
            };
            const newEntityMember = EntityMember.create({
              ...memberInfo,
              legalEntityId: memberInfo.entityId,
            });
            entityMemberInfo.push(newEntityMember);
          }
        }

        // Existing proposal reviewers
        let proposalReviewers: ProposalReviewer[];
        if (session.context?.viewingAsVendor) {
          proposalReviewers = proposal?.vendorReviewers ?? proposal?.vendorReviewers.map((r) => r.clone()) ?? [];
        } else {
          proposalReviewers = proposal?.clientReviewers ?? proposal?.clientReviewers.map((r) => r.clone()) ?? [];
        }

        // Exclude current user and convert to ProposalReviewer array
        let reviewerPool: ProposalReviewer[] = entityMemberInfo
          .filter((reviewer: EntityMember) => reviewer.userId.value !== session.user?.id?.value)
          .map((entityMember: EntityMember) => {
            return new ProposalReviewer({
              entityMember,
              canEdit: false,
            });
          });

        // Merge existing reviewers with the pool
        for (const reviewer of proposalReviewers) {
          let reviewerToAdd = reviewer;
          const existingReviewer = reviewerPool.find((r) => r.userId.isEqualTo(reviewer.userId));
          if (existingReviewer) {
            reviewerPool = reviewerPool.filter((r) => !r.userId.isEqualTo(reviewer.userId));
            reviewerToAdd = new ProposalReviewer({
              entityMember: existingReviewer.entityMember,
              canEdit: reviewer.canEdit,
              dateApproved: reviewer.dateApproved,
              customMessage: reviewer.customMessage,
            });
          }
          reviewerPool.push(reviewerToAdd);
        }

        const reviewerPoolUserIds = reviewerPool.map((r) => r.userId);

        const individuals =
          reviewerPoolUserIds.length > 0
            ? (Individual.fromJSONArray(
                await dispatch(
                  individualsApi.endpoints.getIndividualsByUserIds.initiate({
                    userIds: new Set(reviewerPoolUserIds.map((id) => id.value)),
                  }),
                ).unwrap(),
              ) ?? [])
            : []; // TODO: use redux store

        setReviewerPool(reviewerPool);
        setReviewerResults(reviewerPool);
        setOriginalReviewers(proposalReviewers.map((r) => r.clone()));
        setSelectedReviewers(proposalReviewers.map((r) => r.clone()));
        setReviewerPoolIndividuals(individuals);
      } catch (err) {
        if (err instanceof CanceledError) return;
        console.error(err);
      }
      setIsLoading(false);
    },
    [session, updatedBuilder, proposal, dispatch],
  );

  useEffect(() => {
    if (!popoverAnchor || reviewerPool) return;

    let abortController = new AbortController();

    (async () => {
      try {
        await getReviewers(abortController);
      } catch (err) {
        if (err instanceof CanceledError) return;
        enqueueSnackbar("Failed to load reviewers. Please try again.", { variant: "error" });
        console.error(err);
      }
    })();

    return () => {
      abortController.abort();
      abortController = new AbortController();
    };
  }, [getReviewers, popoverAnchor, reviewerPool]);

  function handleSend() {
    onSend?.(selectedReviewers);
    setReviewerResults(reviewerPool ?? []);
    setSearchTerm("");
    onPopoverClose?.();
  }

  const debouncedSearch = debounce(
    (value) =>
      setReviewerResults(
        reviewerPool?.filter((reviewer) => {
          const individual = reviewerPoolIndividuals.find((ind) => ind.userId.isEqualTo(reviewer.userId));
          return (
            individual?.profile?.fullName.toLowerCase().includes(value.toLowerCase()) ||
            individual?.profile?.email?.toString().toLowerCase().includes(value.toLowerCase())
          );
        }) ?? [],
      ),
    100,
  );

  function search(value: string) {
    setSearchTerm(value);

    if (value === "") {
      setReviewerResults(reviewerPool ?? []);
      return;
    }

    debouncedSearch(value);
  }

  function handleReviewerSelected(event: React.MouseEvent<HTMLDivElement>, reviewer: ProposalReviewer) {
    event.stopPropagation();

    const alreadySelected = selectedReviewers.some((r) => r.userId.isEqualTo(reviewer.userId));
    let updatedReviewers: ProposalReviewer[];

    if (alreadySelected) {
      updatedReviewers = selectedReviewers.filter((r) => r.userId.value !== reviewer.userId.value);
    } else {
      updatedReviewers = [...selectedReviewers, reviewer];
    }
    setSelectedReviewers(updatedReviewers);
    onReviewersChanged?.(updatedReviewers);
  }

  function handleEditToggled(_: ChangeEvent<HTMLInputElement>, editedReviewer: ProposalReviewer) {
    const updatedReviewers = [];

    for (const selectedReviewer of selectedReviewers) {
      let updatedReviewer = selectedReviewer.clone();
      if (selectedReviewer.userId.isEqualTo(editedReviewer.userId)) {
        updatedReviewer = new ProposalReviewer({
          entityMember: selectedReviewer.entityMember,
          canEdit: !selectedReviewer.canEdit,
          dateApproved: selectedReviewer.dateApproved,
          customMessage: selectedReviewer.customMessage,
        });
      }
      updatedReviewers.push(updatedReviewer);
    }

    if (!selectedReviewers.some((selectedReviewer) => editedReviewer.userId.isEqualTo(selectedReviewer.userId))) {
      let addedReviewer = editedReviewer.clone();
      addedReviewer = new ProposalReviewer({
        entityMember: addedReviewer.entityMember,
        canEdit: true,
        dateApproved: addedReviewer.dateApproved,
        customMessage: addedReviewer.customMessage,
      });
      updatedReviewers.push(addedReviewer);
    }
    onReviewersChanged?.(updatedReviewers);
    setSelectedReviewers(updatedReviewers);
  }

  function handleMessagePopoverOpened(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    reviewer?: ProposalReviewer,
  ) {
    const selected = selectedReviewers.find((r) => r.userId.isEqualTo(reviewer?.userId));
    setCurrentReviewerEditing(selected ?? reviewer);
    setPopoverAnchorElement(event?.currentTarget);
  }

  function handleReviewerMessageChanged(userId?: Guid, message?: string) {
    setPopoverAnchorElement(null);

    if (!userId || !selectedReviewers) return;

    // Update the custom message for the reviewer
    const updatedReviewers = [...selectedReviewers].map((r) => {
      if (r.userId.isEqualTo(userId)) {
        return new ProposalReviewer({
          entityMember: r.entityMember,
          canEdit: r.canEdit,
          dateApproved: r.dateApproved,
          customMessage: message,
        });
      }
      return r;
    });

    onReviewersChanged?.(updatedReviewers);
    setSelectedReviewers(updatedReviewers);
  }

  function getMemberType(reviewer: ProposalReviewer): string {
    const team = updatedBuilder?.currentSpec.workAgreement.vendorTeam ?? proposal?.vendorTeam;
    if (Masked.isNotMasked(team) && team?.leader?.userId.isEqualTo(reviewer.userId)) {
      return "Team Leader";
    }
    if (Masked.isNotMasked(team) && team?.members.some((member) => member.userId.isEqualTo(reviewer.userId))) {
      return "Team Member";
    }
    return "Co-worker";
  }

  const addedReviewers = selectedReviewers.filter(
    (r) => !originalReviewers.some((or) => or.userId.isEqualTo(r.userId)),
  );
  const removedReviewers = originalReviewers.filter(
    (or) => !selectedReviewers.some((r) => r.userId.isEqualTo(or.userId)),
  );

  return (
    <Popover
      anchorEl={popoverAnchor}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={popoverAnchor !== undefined}
      onClose={onPopoverClose}
    >
      <PopoverContainer>
        <SearchField
          placeholder="Search for reviewer"
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchTerm !== "" && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm("");
                        search("");
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            },
          }}
          value={searchTerm}
          onChange={(event) => search(event.target.value)}
        />
        {isLoading && <LoaderProgress size={50} disableShrink />}
        {!isLoading && (
          <ListContainer dense>
            {reviewerResults.length < 1 && (
              <ListItem>
                <ListItemText>No Reviewers Found</ListItemText>
              </ListItem>
            )}
            {reviewerResults.length > 0 &&
              reviewerResults.map((reviewer: ProposalReviewer) => {
                const isSelected: boolean = selectedReviewers.some((r) => r.userId.isEqualTo(reviewer.userId));
                const isEditor: boolean = selectedReviewers.some(
                  (r) => r.userId.isEqualTo(reviewer.userId) && r.canEdit,
                );
                const hasApproved: boolean = selectedReviewers.some(
                  (r) => r.userId.isEqualTo(reviewer.userId) && Boolean(r.dateApproved),
                );
                const hasCustomMessage: boolean = selectedReviewers.some(
                  (r) => r.userId.isEqualTo(reviewer.userId) && Boolean(r.customMessage?.trim().length),
                );
                const isNewlyAdded: boolean = addedReviewers.some((r) => r.userId.isEqualTo(reviewer.userId));

                return (
                  <ListItem
                    key={reviewer.userId.value}
                    disablePadding
                    secondaryAction={
                      <ActionsContainer>
                        <EditingSwitch
                          label={isEditor ? "full edit" : "comment only"}
                          labelPlacement="bottom"
                          control={
                            <Switch
                              checked={isEditor}
                              color="primary"
                              onChange={(event) => handleEditToggled(event, reviewer)}
                              onClick={(event) => event.stopPropagation()}
                              size="small"
                            />
                          }
                        />
                        {isSelected && isNewlyAdded && (
                          <Tooltip title={`${hasCustomMessage ? "Edit" : "Add"} Custom Message`}>
                            <Stack sx={{ alignItems: "center" }}>
                              <IconButton size="small" onClick={(event) => handleMessagePopoverOpened(event, reviewer)}>
                                {hasCustomMessage ? (
                                  <CommentIcon color="secondary" fontSize="small" />
                                ) : (
                                  <AddCommentIcon color="secondary" fontSize="small" />
                                )}
                              </IconButton>
                              <MessageLabel variant="caption">
                                {hasCustomMessage ? "edit message" : "add message"}
                              </MessageLabel>
                            </Stack>
                          </Tooltip>
                        )}
                      </ActionsContainer>
                    }
                  >
                    <ListItemButton selected={isSelected} onClick={(event) => handleReviewerSelected(event, reviewer)}>
                      <ReviewerAvatar>
                        {isSelected && (
                          <SelectedIcon>
                            <DoneIcon />
                          </SelectedIcon>
                        )}
                        <IndividualAvatar
                          individual={reviewerPoolIndividuals.find((ind) =>
                            ind.userId.isEqualTo(reviewer.entityMember.userId),
                          )}
                        />
                      </ReviewerAvatar>
                      <ListItemText
                        primary={
                          reviewerPoolIndividuals.find((ind) => ind.userId.isEqualTo(reviewer.entityMember.userId))
                            ?.profile.fullName
                        }
                        secondary={hasApproved ? "Reviewed" : getMemberType(reviewer)}
                        slotProps={{
                          primary: {
                            sx: {
                              color: isSelected ? "primary" : "initial",
                              fontWeight: isSelected ? "bold" : "normal",
                            }
                          },
                          secondary: {
                            color: hasApproved ? "success" : "textSecondary",
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
          </ListContainer>
        )}
        <ButtonContainer>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSend}
            disabled={
              addedReviewers.length < 1 &&
              removedReviewers.length < 1 &&
              !proposal?.isModifiedByBuilder(updatedBuilder) &&
              !proposal?.hasUnsavedRedlineChanges &&
              !proposal?.reviewersAreModified &&
              !arePendingComments &&
              !proposal?.hasUnsharedRedlineChanges
            }
          >
            {getSendButtonLabel({
              addedReviewersCount: addedReviewers.length,
              removedReviewersCount: removedReviewers.length,
              arePendingComments,
              proposalIsModifiedByBuilder: proposal?.isModifiedByBuilder(updatedBuilder) ?? false,
              proposalHasUnsharedRedlineChanges: proposal?.hasUnsharedRedlineChanges ?? false,
            })}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            disabled={
              selectedReviewers.length === originalReviewers.length &&
              selectedReviewers.every((reviewer) => originalReviewers.some((orig) => orig.isEqualTo(reviewer)))
            }
            onClick={() => {
              setSelectedReviewers(originalReviewers);
              setReviewerResults(reviewerPool ?? []);
              setSearchTerm("");
              onReviewersChanged?.(originalReviewers);
            }}
          >
            Reset
          </Button>
        </ButtonContainer>
      </PopoverContainer>
      <ReviewerMessagePopover
        userId={currentReviewerEditing?.userId ?? undefined}
        anchorEl={popoverAnchorElement}
        message={currentReviewerEditing?.customMessage}
        onCanceled={() => setPopoverAnchorElement(null)}
        onMessageChanged={(userId?: Guid, message?: string) => {
          handleReviewerMessageChanged(userId, message);
        }}
      />
    </Popover>
  );
}

export function getSendButtonLabel(args: {
  addedReviewersCount: number;
  removedReviewersCount: number;
  arePendingComments: boolean;
  proposalIsModifiedByBuilder: boolean;
  proposalHasUnsharedRedlineChanges: boolean;
}): string {
  const {
    addedReviewersCount,
    removedReviewersCount,
    arePendingComments,
    proposalIsModifiedByBuilder,
    proposalHasUnsharedRedlineChanges,
  } = args;
  const hasPendingChanges = proposalIsModifiedByBuilder || proposalHasUnsharedRedlineChanges;
  if (addedReviewersCount > 0 && removedReviewersCount === 0) {
    return "Send for Review";
  }
  if (addedReviewersCount === 0 && removedReviewersCount > 0) {
    return "Remove Reviewers";
  }
  if (addedReviewersCount > 0 && removedReviewersCount > 0) {
    return "Update Reviewers";
  }
  if (hasPendingChanges) {
    return "Share Changes";
  }
  if (!hasPendingChanges && arePendingComments) {
    return "Share Comments";
  }
  return "No Updates to Share";
}
